import React from "react";
import Countdown from "react-countdown";

// When the banner copy contains this token, it is replaced with a live
// countdown to the promotion's end date, rendered as `DD::HH::MM::SS`.
const TIMER_TOKEN = "{timer}";

const pad = (value: number) => `${value < 10 ? "0" : ""}${value}`;

// endDate can arrive as an ISO string OR an epoch-millis string (e.g.
// "1780000000000"). `new Date("1780000000000")` is an Invalid Date, so fall
// back to parsing it as a number — same handling as the Pricing countdown.
const parseEndDate = (endDate?: string): Date | null => {
  if (!endDate) return null;

  const asDate = new Date(endDate);
  if (!isNaN(asDate.getTime())) return asDate;

  const asEpoch = parseInt(endDate, 10);
  return isNaN(asEpoch) ? null : new Date(asEpoch);
};

type TBannerContentProps = {
  content?: string;
  endDate?: string;
};

export const BannerContent = ({ content, endDate }: TBannerContentProps) => {
  if (!content) return null;

  // No token — render the copy as-is.
  if (!content.includes(TIMER_TOKEN)) return <>{content}</>;

  const target = parseEndDate(endDate);
  const hasValidTarget = !!target && !isNaN(target.getTime());

  // Splitting on the token always removes it from the rendered copy, so an
  // unconfigured/expired promotion never leaves a literal "{timer}" behind.
  const segments = content.split(TIMER_TOKEN);

  return (
    <>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {segment}
          {index < segments.length - 1 && hasValidTarget && (
            <Countdown
              date={target as Date}
              renderer={({ days, hours, minutes, seconds, completed }) =>
                completed ? null : (
                  // Days unpadded; h/m/s zero-padded so the width stays steady
                  // as it ticks, e.g. "6d 14h 27m 05s".
                  <>{`${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(
                    seconds,
                  )}s`}</>
                )
              }
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
};
